import { Descendant } from "slate";

import { TicketsRoutes } from "constants/tickets/TicketsRoutes";

export interface IAssignmentTeam {
  id: string;
}

export interface IAssignmentAgent {
  id: string;
}

export interface IAssignment {
  team: IAssignmentTeam;
  agent: IAssignmentAgent | null;
}

export interface ICc {
  email: string;
}

export interface IRequester {
  email: string;
  name?: string;
}

export enum DetailedSource {
  Email = "email",
  CreatedManually = "createdManually",
  ChatBot = "chatbot",
  Chat = "chat",
  ContactForm = "contactForm",
  WordPress = "wordpress",
  Webflow = "webflow",
  ContactWidget = "contactWidget",
  Api = "api",
  AiAgent = "aiAgent",
  Copilot = "copilot",
  Workflows = "workflows",
}

/**
 * @deprecated We need this type to map the `SourceType` to `DetailedSource`. Do not use it for new features!
 */
export enum SourceType {
  Email = "email",
  Api = "api",
  Integration = "integration",
  External = "external",
  OpenWidget = "openwidget",
  ChatBot = "chatbot",
  Webflow = "webflow",
  WordPress = "wordpress",
}

export enum Author {
  Agent = "agent",
  Customer = "client",
  System = "system",
  Rule = "rule",
}

export const AuthorLabel: { [key in Author]: string } = {
  [Author.Agent]: "agent",
  [Author.Customer]: "requester",
  [Author.System]: "system",
  [Author.Rule]: "rule",
};

export interface IAssignmentEvent {
  agent?: {
    ID: string;
    name: string;
  };
  team?: {
    ID: string;
    name: string;
  };
}

export interface IRatingEvent {
  rate: Rating;
  comment: string;
  requestedAt: string;
}

export enum IEventAuthorCustomerType {
  Requester,
  Cc,
}

export interface IEventAuthor {
  type: Author;
  id?: string;
  name?: string; // eslint-disable-line no-restricted-globals
  email?: string;
  customerType?: IEventAuthorCustomerType;
}

export enum EventType {
  Message = "message",
  Assignment = "assignment",
  StatusChanged = "status_changed",
  CcChanged = "cc",
  SubjectChanged = "subject",
  RequesterChanged = "requester",
  FollowUpSent = "followUpSent",
  RatingRequestSent = "ratingRequestSent",
  Rating = "rating",
  Spam = "spam",
  PriorityChanged = "priority",
  TagsChanged = "tags",
  FollowersChanged = "followers",
  ChildTickets = "childTickets",
  SiloChanged = "silo",
  Summary = "summary",
}

export interface IOldNew<T> {
  old: T;
  new: T;
}

export interface IEvent {
  id: string;
  date: Date;
  type: EventType;
  isPrivate?: boolean;
  author?: IEventAuthor;
  messageText?: string;
  messageHtml?: string;
  messageRichText?: Descendant[];
  summary?: {
    text?: string;
    html?: string;
    richText?: Descendant[];
  };
  originalMessage?: {
    html: string;
    text: string;
  };
  stripped?: boolean;
  uncutContent?: {
    html: string;
    text: string;
  };
  source?: ITicketEventSource;
  assignment?: IOldNew<IAssignmentEvent>;
  rating?: IOldNew<IRatingEvent>;
  ratingRequest?: {
    text: string;
  };
  status?: IOldNew<TicketStatus>;
  silo?: IOldNew<TicketSilo>;
  priority?: IOldNew<TicketPriority>;
  deliveryStatus?: ITicketDeliveryStatus[];
  subject?: IOldNew<string>;
  requester?: IOldNew<IRequester>;
  spam?: IOldNew<ITicketSpamStatus>;
  tags?: IOldNew<string[]>;
  followers?: IOldNew<{ ID: string; name: string }[]>;
  cc?: IOldNew<ICc[]>;
  spamReasonSource?: boolean;
  from?: {
    id: string;
    name: string; // eslint-disable-line no-restricted-globals
  };
  to?: {
    id: string;
    name: string; // eslint-disable-line no-restricted-globals
  };
  current?: string;
  previous?: string;
  attachments?: IAttachment[];
  childTickets?: IChildTickets;
  replySuggestionIDs?: string[];
}
export interface IChildTickets {
  diff: IChildTicket[];
  old: IChildTicket[];
  new: IChildTicket[];
}

export interface IChildTicket {
  subject: string;
  ID: string;
  shortID: string;
}

export enum TicketStatus {
  Open = "open",
  Pending = "pending",
  OnHold = "onhold",
  Solved = "solved",
  Closed = "closed",
}

export const TicketStatusFormattedValues: { [key in TicketStatus]: string } = {
  [TicketStatus.Open]: "Open",
  [TicketStatus.Pending]: "Pending",
  [TicketStatus.OnHold]: "On hold",
  [TicketStatus.Solved]: "Solved",
  [TicketStatus.Closed]: "Closed",
};

export enum TicketDeliveryStatus {
  Rejected = "rejected",
  Bounced = "bounced",
  Spam = "spam",
  Delivered = "delivered",
  Opened = "opened",
  Unsent = "unsent",
  SizeExceeded = "sizeExceeded",
  Failed = "failed",
  Unsubscribed = "unsubscribed",
}

export interface ITicketDeliveryStatus {
  entityId: string;
  entityType: Author;
  status: TicketDeliveryStatus;
  date: Date;
}

export enum TicketSourceReferenceType {
  Lc2 = "lc2",
  ChatBot = "chatbot",
}

export interface ITicketSource {
  detailedSource: DetailedSource;
  referenceId?: string;
  referenceType?: TicketSourceReferenceType;
  referenceUrl?: string;
  mailboxId?: string;
  contactFormId?: string;
  from?: string;
  isBooster?: boolean;
}

export type ITicketEventSource = ITicketSource;

export enum Rating {
  Good = "good",
  Neutral = "neutral",
  Bad = "bad",
}

export interface IAttachment {
  contentType: string;
  name: string;
  url: string;
  width?: number;
  height?: number;
  removedAt: Date;
  external: boolean;
  eventID: number;
}

export enum TicketSpamReason {
  HeaderSpam = "headerSpam",
  HeaderAutogenerated = "headerAutogenerated",
  LicenseBlackList = "licenseBlackList",
  LimitPerHour = "limitPerHour",
  SimilarEmail = "similarEmail",
  TooManyRecipients = "tooManyRecipients",
  MalwareDomains = "malwareDomains",
  MalwareAttachments = "malwareAttachments",
}

export interface ITicketSpamStatus {
  status: boolean;
  reason: TicketSpamReason | null;
}

export interface IIntegrations {
  livechat?: {
    externalId: string;
  };
}

export interface ITicketCustomFields {
  vin?: string;
}

export interface IReplyPaths {
  ID: string;
  createdAt: string;
  createdBy: string;
  suggestedPaths: ISuggestedPrompt[];
}

export interface ISuggestedPrompt {
  pathID: string;
  text: string;
}

export interface ITicket {
  id: string;
  shortId: string;
  date: Date;
  createdBy?: string;
  updatedAt?: Date;
  updatedBy?: string;
  lastMessageAt?: Date;
  events: IEvent[];
  requester: IRequester;
  status: TicketStatus;
  silo: TicketSilo;
  spam: ITicketSpamStatus;
  subject: string;
  source: ITicketSource | null;
  tagIds: string[];
  teamIds: string[];
  assignment: IAssignment;
  ratingRequestSent: boolean;
  totalRatings: number;
  rating: Rating | null;
  ccs: ICc[] | null;
  draft?: IDraft;
  integrations?: IIntegrations;
  priority: TicketPriority;
  followers: string[];
  childTickets: IMergedTicket[];
  parentTicket: IMergedTicket | null;
  customFields?: ITicketCustomFields;
  detectedLanguage: string | null;
  deliveryStatus?: TicketDeliveryStatus;
  replyPaths?: IReplyPaths;
}

export interface IMergedTicket {
  ID: string;
  shortID: string;
  subject: string;
}

export interface ISimilarTicket {
  id: string;
  subject: string;
}

export interface IDraft {
  message: string | Descendant[];
  isPrivate?: boolean;
  status?: TicketStatus;
  subject?: string;
  requesterName?: string;
  requesterEmail?: string;
  assigneeId?: string;
  assignedTeamId?: string;
  priority?: string;
  tags?: string[];
  cannedResponseIds?: string[];
  peopleInTheLoop: string[];
  replySuggestionIds?: string[];
}

export enum TicketPriority {
  Low = -10,
  Medium = 0,
  High = 10,
  Urgent = 20,
}

export enum TicketSilo {
  Archive = "archive",
  Spam = "spam",
  Trash = "trash",
  Tickets = "tickets",
}

export const siloTabNames: Record<TicketSilo, string> = {
  [TicketSilo.Tickets]: "All recent tickets",
  [TicketSilo.Archive]: "Archive",
  [TicketSilo.Trash]: "Trash",
  [TicketSilo.Spam]: "Spam",
};

export const getSiloByRoute = (route: TicketsRoutes | null): TicketSilo => {
  switch (route) {
    case TicketsRoutes.Archive:
      return TicketSilo.Archive;
    case TicketsRoutes.Spam:
      return TicketSilo.Spam;
    case TicketsRoutes.Trash:
      return TicketSilo.Trash;
    default:
      return TicketSilo.Tickets;
  }
};

export const TicketSiloFormattedValues: { [key in TicketSilo]: string } = {
  [TicketSilo.Archive]: "Archive",
  [TicketSilo.Spam]: "Spam",
  [TicketSilo.Trash]: "Trash",
  [TicketSilo.Tickets]: "Recent tickets",
};

export enum TicketActionTypes {
  SetStatus = "set-status",
  SetPriority = "set-priority",
  AssignTeam = "assign-team",
  AssignAgent = "assign-agent",
  AddTag = "add-tag",
  MoveToArchive = "move-to-archive",
  MoveToSpam = "move-to-spam",
  MoveToTrash = "move-to-trash",
  Restore = "restore",
  Unarchive = "unarchive",
  NotSpam = "not-spam",
  DeleteForever = "delete-forever",
}

export const TicketSiloToActionType: { [key in TicketSilo]: TicketActionTypes } = {
  [TicketSilo.Archive]: TicketActionTypes.MoveToArchive,
  [TicketSilo.Spam]: TicketActionTypes.MoveToSpam,
  [TicketSilo.Trash]: TicketActionTypes.MoveToTrash,
  [TicketSilo.Tickets]: TicketActionTypes.Restore,
};
